import i18next from 'i18next'
import { MRT_Localization } from 'material-react-table'
import { MRT_Localization_DE } from 'material-react-table/locales/de'
import { MRT_Localization_EN } from 'material-react-table/locales/en'
import { MRT_Localization_FI } from 'material-react-table/locales/fi'
import { MRT_Localization_NO } from 'material-react-table/locales/no'
import { MRT_Localization_SV } from 'material-react-table/locales/sv'
import { initReactI18next } from 'react-i18next'
import { ACTIVE_SYSTEM_LOCAL_STORAGE_KEY } from 'store/auth/auth'
import { ExperimentName, isExperimentEnabled } from 'utils/experiments/experiments'
import createCustomBackend from './customBackend'
import { createMissingKeyReporter, reportMissingTranslationToSentry } from './missingKeyReporter'
import { createFilesBackend } from './translationLoader'

const language = (localStorage.getItem('language') ?? localizationMapping(getWindowLocale())) as LanguageCode

const activeSystemItem = localStorage.getItem(ACTIVE_SYSTEM_LOCAL_STORAGE_KEY)
const activeSystem: System | null = activeSystemItem ? JSON.parse(activeSystemItem) : null
const overrideToken = activeSystem?.custom_translation_token ?? undefined
const translationSource = isExperimentEnabled(ExperimentName.EnableTranslationFiles) ? 'files' : 'transifex'
const mainBackend =
  translationSource === 'files'
    ? createFilesBackend(overrideToken)
    : createCustomBackend({ tokens: [process.env.REACT_APP_TX_TOKEN, ...(overrideToken ? [overrideToken] : [])] })

const FALLBACK_LANGUAGE = 'en'
const missingTranslations = createMissingKeyReporter({
  hasLoaded: (language, namespace) =>
    i18next.hasResourceBundle(language, namespace) && i18next.hasResourceBundle(FALLBACK_LANGUAGE, namespace),
  report: reportMissingTranslationToSentry(translationSource),
  reportFallbackFor: ['sv'],
})

i18next
  .use(initReactI18next)
  .use(mainBackend)
  .use(missingTranslations.fallbackReporter)
  .init({
    lng: language,
    fallbackLng: FALLBACK_LANGUAGE, // use en if detected lng is not available
    defaultNS: 'translation',
    ns: ['translation'],
    fallbackNS: 'translation',
    keySeparator: false, // we do not use keys in form messages.welcome in transifex
    saveMissing: true,
    saveMissingTo: 'current',
    saveMissingPlurals: false,
    missingKeyHandler: missingTranslations.handleMissingKey,
    postProcess: [missingTranslations.fallbackReporter.name],
    postProcessPassResolved: true,
    interpolation: {
      escapeValue: false,
    },
    i18nFormat: {
      bindI18nStore: 'added',
    },
    react: {
      bindI18nStore: 'added',
      useSuspense: true,
    },
  })

export default i18next

export function getLanguage(): LanguageCode {
  return (localStorage.getItem('language') ?? localizationMapping(getWindowLocale())) as LanguageCode
}

export function getWindowLocale(): string {
  return window.locale ?? `en-GB`
}

export function localizationMapping(locale: string): LanguageCode {
  const [primaryLanguage] = locale.toLowerCase().split(/[-_]/)
  switch (primaryLanguage) {
    case 'sv':
      return 'sv'
    case 'nb':
    case 'no':
    case 'nn':
      return 'nb'
    case 'fi':
      return 'fi'
    case 'de':
      return 'de'
    default:
      return 'en'
  }
}

export function languageToTimeZone(language: LanguageCode): string {
  switch (language) {
    case 'sv':
      return 'Europe/Stockholm'
    case 'en':
      return 'Europe/Stockholm'
    case 'nb':
      return 'Europe/Oslo'
    case 'fi':
      return 'Europe/Helsinki'
    case 'de':
      return 'Europe/Berlin'
    default:
      return 'Europe/Stockholm'
  }
}

export function languageToLocalization(language: LanguageCode): string {
  switch (language) {
    case 'sv':
      return 'sv-SE'
    case 'nb':
      return 'nb-NO'
    case 'en':
      return 'en-GB'
    case 'fi':
      return 'fi-FI'
    case 'de':
      return 'de-DE'
    default:
      return 'en-GB'
  }
}

export function getMRTUserLocalization(localization: LanguageCode | undefined): MRT_Localization {
  switch (localization) {
    case 'sv':
      return MRT_Localization_SV
    case 'nb':
      return MRT_Localization_NO
    case 'fi':
      return MRT_Localization_FI
    case 'de':
      return MRT_Localization_DE
    default:
      return MRT_Localization_EN
  }
}
